"use client"

import type { ComponentProps } from "react"
import type { RecipeVariants } from "@vanilla-extract/recipes"
import clsx from "clsx"

import type { Nullish } from "~/@types/generics"
import type { SbRichtext } from "~/lib/storyblok/schemas/default/richtext"
import useTruncateRichText from "~/hooks/useTruncateRichText"
import { Image } from "~/components/ui/Image"
import type { TImage } from "~/components/ui/Image/_data/serializer"
import InlineCta, { type InlineCtaProps } from "~/components/ui/InlineCta"
import RichText from "~/components/ui/RichText"
import SquareCta from "~/components/ui/SquareCta"
import type { SliceWithProps } from "~/components/slices/SliceManager"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import { sprinkles } from "~/styles/sprinkles.css"
import { breakpoints } from "~/styles/variables/breakpoints"

import * as css from "./styles.css"

const SIZES = [{ breakpoint: breakpoints.md, ratio: 1 / 2 }, { ratio: 1 }]

export type TPushEditoImage = {
  image: TImage
  headline: string
  description?: Nullish<SbRichtext>
  link?: Nullish<InlineCtaProps>
  buttonType: "squared" | "inline"
  descriptionIsTruncated: boolean
} & RecipeVariants<typeof css.description> &
  RecipeVariants<typeof css.title> &
  RecipeVariants<typeof css.PushEditoImage>

export type PushEditoImageProps = SliceWithProps<ComponentProps<"div"> & TPushEditoImage>

function PushEditoImage({
  className,
  image,
  headline,
  description,
  link,
  isFirstSlice,
  verticalAlign = "center",
  textSize = "medium",
  buttonType = "inline",
  font = "primary",
  descriptionIsTruncated = true,
  isLastSlice: _, // extract isLastSlice prop to avoid DOM attributes error
  ...rest
}: PushEditoImageProps) {
  // const { ref: descriptionRef, state: truncateState, className: truncateClassName } = useTruncateText()
  const { setIsReadMore, isReadMore, text } = useTruncateRichText(description, 250)

  const t = useTranslate()
  return (
    <div data-comp="Slices/PushEditoImage" className={clsx(className, css.PushEditoImage({ verticalAlign }))} {...rest}>
      <div className={clsx(css.container)}>
        <p className={clsx(css.title({ textSize, font }))}>{headline}</p>
        <div className={clsx(css.descriptionContainer)}>
          <RichText className={clsx(css.description({ textSize }))} render={text} disableStyles />
        </div>
        {descriptionIsTruncated && isReadMore ? (
          <InlineCta
            className={css.truncatedCta}
            color="black"
            withLine
            withHover={false}
            withDefaultLine
            size="small"
            onClick={() => setIsReadMore(false)}
          >
            {t("cta_read_more")}
          </InlineCta>
        ) : (
          <InlineCta
            className={css.truncatedCta}
            color="black"
            withLine
            withHover={false}
            withDefaultLine
            size="small"
            onClick={() => setIsReadMore(true)}
          >
            {t("cta_read_less")}
          </InlineCta>
        )}

        {buttonType === "inline" && link && (
          <InlineCta
            className={sprinkles({ fontWeight: "medium" })}
            {...link}
            color="black"
            size="small"
            withDefaultLine
          />
        )}
        {buttonType === "squared" && link && (
          <SquareCta className={sprinkles({ fontWeight: "medium" })} {...link} theme="backgroundBlack" size="small" />
        )}
      </div>

      <Image
        className={clsx(css.image)}
        {...image}
        ratio="375_580"
        sizesFromBreakpoints={SIZES}
        asPlaceholder
        priority={isFirstSlice}
      />
    </div>
  )
}

export default PushEditoImage
